import React, { useCallback, useEffect, useRef } from "react";
import { CHART, chartHeight, itemKey, projectYear } from "./config";
import TimelineRow from "./TimelineRow";
import { useDragToScroll } from "./useDragToScroll";

// Hold the name column against the left edge, and the year axis against the
// top, while the chart moves under them.
//
// Both are SVG children, so neither can be `position: sticky`. They are
// translated instead, by offsets written to CSS custom properties on the <svg>
// rather than to React state: a state change here would re-render every row of
// an eighty-row chart on every frame of a scroll, and a chart that stutters
// when dragged is worse than one whose names run away.
//
// The names follow the FRAME's horizontal scroll; the axis follows the PAGE's
// vertical scroll, because the chart is taller than the window and the years
// are otherwise gone by the third row. Both offsets are CSS pixels and the SVG
// is drawn at whatever the zoom stretched it to, so they only agree with the
// user units they are applied in after dividing by the scale the browser
// actually resolved.
function usePinned(scrollRef, svgRef) {
  const frame = useRef(null);

  const measure = useCallback(() => {
    const scroller = scrollRef.current;
    const svg = svgRef.current;
    if (!scroller || !svg) return;

    const box = svg.getBoundingClientRect();
    // Zero while the chart is display:none or mid-layout. Nothing to pin to.
    const scale = box.width ? box.width / CHART.width : 0;
    const names = scale ? scroller.scrollLeft / scale : 0;

    // How much of the chart has gone off the top of the window. The axis stops
    // short of the foot rather than riding past the last row: once the
    // chronology has ended it would be labelling nothing.
    const height = svg.viewBox?.baseVal?.height || 0;
    const floor = Math.max(0, height - CHART.axisFloor);
    const axis = scale
      ? Math.min(Math.max(0, -box.top) / scale, floor)
      : 0;

    svg.style.setProperty("--history-label-shift", `${names}px`);
    svg.style.setProperty("--history-axis-shift", `${axis}px`);
    svg.dataset.labelShift = String(Math.round(names));
    svg.dataset.axisShift = String(Math.round(axis));
    svg.dataset.pinned = names > 0.5 ? "true" : "false";
    svg.dataset.axisFloating = axis > 0.5 ? "true" : "false";
  }, [scrollRef, svgRef]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;

    // Scroll fires far faster than a frame; one measurement per frame is all
    // the eye gets anyway.
    const schedule = () => {
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        measure();
      });
    };

    measure();
    scroller.addEventListener("scroll", schedule, { passive: true });
    // The page's own scroll is what moves the axis, and it is not the frame's.
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(scroller);

    return () => {
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [measure, scrollRef]);

  return measure;
}

export default function TimelineSvg({ handlersFor, periodMap, state, view, zoom }) {
  const height = chartHeight(view.rows.length);
  const x = (year) => projectYear(year, view.axis);
  const scrollRef = useRef(null);
  const svgRef = useRef(null);
  const measure = usePinned(scrollRef, svgRef);
  useDragToScroll(scrollRef, svgRef);

  // A zoom or a change of layers redraws the chart at a new width; the pin has
  // to be recomputed against it or the names sit at the old scale.
  useEffect(measure, [measure, height, zoom]);

  // minWidth is what makes the chart SCROLL rather than shrink: squeezing 1290
  // units into a 375px phone renders the names at 4px, which is not a chart of
  // anything.
  return (
    <div
      className="history-chart__scroll"
      ref={scrollRef}
      tabIndex="0"
      aria-label="Zoomable chronology"
      aria-describedby="history-chart-pan-hint"
    >
      <svg
        className="history-chart__svg"
        ref={svgRef}
        data-zoom={zoom}
        style={{ width: `${zoom * 100}%`, minWidth: `${CHART.minimumPixelWidth * zoom}px` }}
        viewBox={`0 0 ${CHART.width} ${height}`}
        role="group"
        aria-labelledby="history-chart-title history-chart-description"
      >
        <title id="history-chart-title">A selectable chronology of Western esoteric history</title>
        <desc id="history-chart-description">
          {view.summary} Horizontal marks are lives or composition windows. Diamonds are events,
          triangles are figures in cultural layers, and squares or bars are texts. Hollow dashed
          points are schematic or legendary rather than historical dates.
        </desc>
        {view.axis.ticks.map((tick) => (
          <line
            key={tick.year}
            className="history-chart__grid"
            x1={x(tick.year)}
            y1="42"
            x2={x(tick.year)}
            y2={height - 10}
          />
        ))}
        {view.rows.map((item, index) => (
          <TimelineRow
            key={itemKey(item)}
            handlersFor={handlersFor}
            item={item}
            periodMap={periodMap}
            state={state}
            x={x}
            y={CHART.top + (index * CHART.rowHeight)}
          />
        ))}
        {/* Painted after the rows, so the years float over them rather than
            under. The grid lines above stay where they are: they are the
            chart's own scale, not its labels. */}
        <g className="history-chart__axis">
          <rect
            className="history-chart__axis-backdrop"
            x="0"
            y="4"
            width={CHART.width}
            height={CHART.axisHeight}
          />
          <line
            className="history-chart__axis-edge"
            x1="0"
            y1={CHART.axisHeight + 4}
            x2={CHART.width - CHART.rightPadding}
            y2={CHART.axisHeight + 4}
          />
          {view.axis.ticks.map((tick) => (
            <text
              key={tick.year}
              className="history-chart__tick-label"
              x={x(tick.year)}
              y="25"
              textAnchor="middle"
            >
              {tick.label}
            </text>
          ))}
        </g>
        {view.rows.length === 0 && (
          <text className="history-chart__empty" x={CHART.width / 2} y="92" textAnchor="middle">
            Choose a timeline layer above.
          </text>
        )}
      </svg>
    </div>
  );
}
